import { useCallback, useEffect, useRef, useState } from "react";

const WS_URL = process.env.REACT_APP_WS_URL;
const API_URL = process.env.REACT_APP_API_URL;

const emojiList = ["😀", "😂", "😍", "🙏", "🔥", "👍", "✅", "🎉", "💬", "😎"];

const UseModalChat = () => {
  const [isOpenChat, setIsOpenChat] = useState(false);
  const [selectedNegotiationChat, setSelectedNegotiationChat] = useState(null);
  const [currentRoleChat, setCurrentRoleChat] = useState("");
  const [messagesChat, setMessagesChat] = useState([]);
  const [messageChat, setMessageChat] = useState("");
  const [showEmojiPickerChat, setShowEmojiPickerChat] = useState(false);
  const [loadingMessagesChat, setLoadingMessagesChat] = useState(false);
  const [sendingChat, setSendingChat] = useState(false);
  const [errorChat, setErrorChat] = useState("");

  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);

  const getValidToken = async () => {
    const res = await fetch(`${API_URL}/api/auth/refresh/`, {
      method: "POST",
      credentials: "include",
    });

    if (!res.ok) throw new Error("Sem sessão");

    const data = await res.json();
    return data.access;
  };

  const closeChatModal = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsOpenChat(false);
    setSelectedNegotiationChat(null);
    setCurrentRoleChat("");
    setMessagesChat([]);
    setShowEmojiPickerChat(false);
    setMessageChat("");
    setLoadingMessagesChat(false);
    setSendingChat(false);
    setErrorChat("");
  }, []);

  const openChatModal = (negotiation, role) => {
    setSelectedNegotiationChat(negotiation);
    setCurrentRoleChat(role);
    setIsOpenChat(true);
    setMessagesChat([]);
    setMessageChat("");
    setShowEmojiPickerChat(false);
    setErrorChat("");
  };

  useEffect(() => {
    const connect = async () => {
      if (!isOpenChat || !selectedNegotiationChat?.serie_trip_negotiations)
        return;

      setLoadingMessagesChat(true);

      try {
        const token = await getValidToken();

        const ws = new WebSocket(
          `${WS_URL}/ws/chat/${selectedNegotiationChat.serie_trip_negotiations}/?token=${encodeURIComponent(token)}&role=${encodeURIComponent(currentRoleChat)}`,
        );

        wsRef.current = ws;

        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);

          if (data.type === "history") {
            setMessagesChat(data.results || []);
            setLoadingMessagesChat(false);
            return;
          }

          setMessagesChat((prev) => [...prev, data]);
        };

        ws.onerror = () => {
          setErrorChat("Não foi possível conectar ao chat.");
        };

        ws.onclose = () => {
          setLoadingMessagesChat(false);
        };
      } catch (err) {
        setErrorChat("Sessão expirada. Faça login novamente.");
        setLoadingMessagesChat(false);
      }
    };

    connect();

    return () => {
      wsRef.current?.close();
    };
  }, [isOpenChat, selectedNegotiationChat, currentRoleChat]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messagesChat, isOpenChat]);

  const handleMessageChange = (value) => {
    setMessageChat(value);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();

    const cleanMessage = messageChat.trim();
    if (
      !cleanMessage ||
      !wsRef.current ||
      wsRef.current.readyState !== WebSocket.OPEN
    )
      return;

    setSendingChat(true);
    wsRef.current.send(
      JSON.stringify({
        type: "message",
        message: cleanMessage,
        sender_role: currentRoleChat,
      }),
    );
    setMessageChat("");
    setSendingChat(false);
  };

  const handleEmojiSelect = (emoji) => {
    setMessageChat((prev) => `${prev}${emoji}`);
    setShowEmojiPickerChat(false);
  };

  return {
    isOpenChat,
    selectedNegotiationChat,
    currentRoleChat,
    messagesChat,
    messageChat,
    emojiList,
    setShowEmojiPickerChat,
    showEmojiPickerChat,
    handleEmojiSelect,
    loadingMessagesChat,
    sendingChat,
    errorChat,
    messagesEndRef,
    openChatModal,
    closeChatModal,
    handleMessageChange,
    handleSendMessage,
  };
};

export default UseModalChat;
